import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Connection } from '../obj/Connection';
import { StorageRouterService } from '../services/storage-router.service';
import { FlowManagementService } from '../services/flow-management.service';

/**
 * Route landing for `/connector/:envName/:connectorName/edit`.
 *
 * Shows the Connector (API-definition) JSON for a given connector name like
 * `shared_sql` or `shared_sharepointonline`. Connectors are tenant-shared
 * resources — multiple Connection instances point at the same one — so the
 * URL is keyed on the connector name rather than a specific connection.
 *
 * `FlowDetailComponent` in editorType="Connector" mode reads `connection.connector`
 * (computed from `connection.apiId`) and `connection.environment` to build the
 * upstream URL. We synthesize a minimal Connection instance carrying just
 * those two fields — no specific tenant connection is needed.
 */
@Component({
    selector: 'app-connector-edit',
    templateUrl: './connector-edit.component.html',
    // Reuse connection-edit's stylesheet so both pages share identical chrome.
    // Angular's emulated encapsulation re-scopes the CSS per component, so the
    // class names work the same way under each component's attribute.
    styleUrls: ['./connection-edit.component.scss'],
    standalone: false,
})
export class ConnectorEditComponent implements OnInit, OnDestroy {
    public connectorName = '';
    public envName = '';
    /** Synthesized to satisfy app-flow-detail's @Input() connection contract. */
    public connectorStub: Connection | null = null;
    public error = '';
    public loading = true;

    private subscription = new Subscription();

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private storage: StorageRouterService,
        private flowManagementService: FlowManagementService,
    ) {}

    async ngOnInit(): Promise<void> {
        this.envName = this.route.snapshot.paramMap.get('envName') || '';
        this.connectorName = this.route.snapshot.paramMap.get('connectorName') || '';
        if (!this.envName || !this.connectorName) {
            this.error = 'Missing connector route parameters.';
            this.loading = false;
            return;
        }

        this.flowManagementService.currentEnvironment = this.envName;

        // Build a stub Connection. The constructor expects a raw API-shape
        // payload — `apiId` round-trips through `connection.connector`'s regex
        // extractor (see Connection.ts) so getConnector() produces the right
        // upstream URL.
        const stub = new Connection({
            name: this.connectorName,
            id: this.connectorName,
            properties: {
                apiId: `/providers/Microsoft.PowerApps/apis/${this.connectorName}`,
                displayName: this.connectorName,
                iconUri: '',
                statuses: [],
                environment: { name: this.envName },
                metadata: {},
            },
        });
        this.connectorStub = stub;

        // Try to enrich displayName/iconUri from any cached connection that
        // happens to use this connector — purely cosmetic, the underlying
        // editor still resolves Connector data from the upstream API.
        try {
            const cached = await this.storage.getConnections(this.envName);
            const sample = cached.find((c) => c.connector === this.connectorName);
            if (sample) {
                stub.displayName = sample.displayName || this.connectorName;
                stub.iconUri = sample.iconUri || '';
            }
        } catch { /* best-effort */ }

        this.loading = false;
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public backToConnections(): void {
        this.router.navigate(['/connections']);
    }
}
